/**
 * ExtrasPage - View and manage treats
 * - Read-only display of current treats
 * - "Edit" button opens modal to change quantities or remove
 * - "Add Treats" button opens multi-step wizard to add new treats
 */

import { useMemo, useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Dialog,
  Alert,
  Chip,
} from "@mui/material";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import type { RechargeSubscription } from "../types";
import { buildVariantIndex, collectDogAllergies } from "../utils/recharge";
import { filterRecipesByAllergies } from "@/helpers/mealDistribution";
import { QuizStyledCard } from "../components/shared/QuizStyledCard";
import { PageTitle } from "../components/shared/PageTitle";
import { QuizButton } from "../components/shared/QuizButton";
import { PawsLoader } from "../components/shared/PawsLoader";
import { AddTreatsWizard } from "../components/AddTreatsWizard";
import { changeQuantity, cancelSubscription, getOnetimesForAddress, deleteOnetime, updateOnetimeQuantity } from "../api/accountProxy";
import { useAccount } from "../context/AccountContext";
import { QUIZ } from "@/theme/quizTokens";

type TreatProduct = {
  productId: string;
  productTitle: string;
  imageUrl: string | null;
  variantId: string;
  variantTitle: string;
  price: number;
};

export function ExtrasPage({
  subs,
  products,
  proxyBase,
  shop,
  customerId,
  onOnetimesChanged,
}: {
  subs: RechargeSubscription[];
  products: any[];
  proxyBase: string;
  shop: string;
  customerId?: string;
  onOnetimesChanged?: () => void;
}) {
  const { showLoading, hideLoading, refresh, addError } = useAccount();

  const variantIndex = useMemo(
    () => buildVariantIndex(products || []),
    [products]
  );

  const [addWizardOpen, setAddWizardOpen] = useState(false);

  // Individual treat edit state
  const [editingTreat, setEditingTreat] = useState<{
    id: string;
    productTitle: string;
    imageUrl: string | null;
    price: number;
    quantity: number;
  } | null>(null);
  const [editQuantity, setEditQuantity] = useState(1);
  const [editSaving, setEditSaving] = useState(false);
  const [editSuccess, setEditSuccess] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Pending one-times state
  const [pendingOnetimes, setPendingOnetimes] = useState<any[]>([]);
  const [loadingOnetimes, setLoadingOnetimes] = useState(true);
  const [editingOnetime, setEditingOnetime] = useState<{
    id: string;
    productTitle: string;
    imageUrl: string | null;
    price: number;
    quantity: number;
  } | null>(null);
  const [onetimeQuantity, setOnetimeQuantity] = useState(1);
  const [onetimeSaving, setOnetimeSaving] = useState(false);
  const [onetimeSuccess, setOnetimeSuccess] = useState(false);
  const [onetimeError, setOnetimeError] = useState<string | null>(null);

  // Get primary address ID from subscriptions
  const primaryAddressId = useMemo(() => {
    for (const sub of subs) {
      const addressId = (sub as any).address_id;
      if (addressId) return String(addressId);
    }
    return null;
  }, [subs]);

  // Fetch pending one-times for primary address
  useEffect(() => {
    if (!primaryAddressId) {
      setLoadingOnetimes(false);
      return;
    }
    setLoadingOnetimes(true);
    getOnetimesForAddress(proxyBase, shop, primaryAddressId, customerId)
      .then((result) => {
        if (result.ok) {
          setPendingOnetimes(result.onetimes || []);
        } else if (result.error) {
          addError({
            message: result.error || "Failed to load pending add-ons",
            context: "Extras Page",
            code: "FETCH_ONETIMES_FAILED",
          });
        }
      })
      .catch((err: any) => {
        addError({
          message: err?.message || "Network error loading add-ons",
          context: "Extras Page",
          code: "FETCH_ONETIMES_NETWORK",
        });
      })
      .finally(() => {
        setLoadingOnetimes(false);
      });
  }, [primaryAddressId, proxyBase, shop, customerId, addError]);

  // Check if customer has any subscriptions/addresses
  const hasSubscriptions = subs.length > 0;

  // Get the next delivery date from any active subscription
  const nextDeliveryDate = useMemo(() => {
    for (const sub of subs) {
      const nextCharge = (sub as any).next_charge_scheduled_at;
      if (nextCharge) return nextCharge;
    }
    return null;
  }, [subs]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Not scheduled";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
      });
    } catch (err) {
      console.warn("[ExtrasPage] Failed to format date:", dateStr, err);
      return dateStr;
    }
  };

  // Get current treat subscriptions
  const currentTreatSubs = useMemo(() => {
    return subs
      .filter((sub) => {
        // Skip bundle subscriptions (they have _paws_meals_json)
        const props = (sub as any).properties || [];
        const isBundle = props.some((p: any) => p.name === "_paws_meals_json");
        if (isBundle) return false;

        // Check if this product is a treat
        const variantId = String(
          (sub as any).shopify_variant_id ||
            (sub as any).external_variant_id ||
            ""
        );
        const info = variantIndex.get(variantId);
        return info?.tags?.includes("Treats");
      })
      .map((sub) => {
        const variantId = String(
          (sub as any).shopify_variant_id ||
            (sub as any).external_variant_id ||
            ""
        );
        const info = variantIndex.get(variantId);
        return {
          id: String(sub.id),
          variantId,
          productTitle: info?.productTitle || (sub as any).product_title || "Treat",
          variantTitle: info?.variantTitle || "",
          imageUrl: info?.imageUrl || null,
          price: Number((sub as any).price || info?.price || 0),
          quantity: Number((sub as any).quantity || 1),
          nextCharge: (sub as any).next_charge_scheduled_at,
        };
      });
  }, [subs, variantIndex]);

  // Get available treats from products (first variant of each treat product)
  const availableTreats = useMemo(() => {
    const treats: TreatProduct[] = [];

    for (const pWrap of products || []) {
      const p = pWrap?.product ?? pWrap;
      if (!p?.variants?.length) continue;

      // Check if this is a treat product (has Treats tag)
      const tags = p.tags || [];
      const isTreat = Array.isArray(tags)
        ? tags.some((t: string) => t === "Treats" || t.includes("Treat"))
        : String(tags).includes("Treat");
      if (!isTreat) continue;

      // Get first variant
      const firstVariant = p.variants[0];
      if (!firstVariant) continue;

      const variantId = String(firstVariant.id || "");

      // Get image
      let imageUrl: string | null = null;
      if (p.image?.src) {
        imageUrl = p.image.src;
      } else if (Array.isArray(p.images) && p.images[0]) {
        imageUrl = typeof p.images[0] === "string" ? p.images[0] : p.images[0]?.src;
      }

      treats.push({
        productId: String(p.id || ""),
        productTitle: p.title || "Treat",
        imageUrl,
        variantId,
        variantTitle: firstVariant.title || "",
        price: Number(firstVariant.price) || 0,
      });
    }

    // Extras are household-level, so filter by the union of every dog's
    // allergies (same rules as the quiz: keywords, synonyms, hidden allergens).
    const allergies = collectDogAllergies(subs);
    const safe = treats.filter(
      (t) => filterRecipesByAllergies([{ title: t.productTitle }], allergies).length > 0
    );

    return safe.sort((a, b) => a.productTitle.localeCompare(b.productTitle));
  }, [products, subs]);

  // Calculate total
  const treatsTotal = currentTreatSubs.reduce(
    (sum, t) => sum + t.price * t.quantity,
    0
  );

  const handleAddSuccess = () => {
    refresh();
  };

  // Open edit modal for a specific treat
  const openEditTreat = (treat: typeof currentTreatSubs[0]) => {
    setEditingTreat({
      id: treat.id,
      productTitle: treat.productTitle,
      imageUrl: treat.imageUrl,
      price: treat.price,
      quantity: treat.quantity,
    });
    setEditQuantity(treat.quantity);
    setEditSuccess(false);
    setEditError(null);
  };

  const closeEditTreat = () => {
    setEditingTreat(null);
    setEditQuantity(1);
    setEditSuccess(false);
    setEditError(null);
  };

  // Save quantity change
  const handleSaveQuantity = async () => {
    if (!editingTreat || editQuantity === editingTreat.quantity) return;

    setEditSaving(true);
    setEditError(null);
    showLoading("Updating treat quantity...");

    try {
      await changeQuantity(proxyBase, shop, editingTreat.id, editQuantity, customerId);
      setEditSuccess(true);
      refresh();
      setTimeout(() => closeEditTreat(), 1200);
    } catch (err: any) {
      setEditError(err.message || "Failed to update quantity");
    } finally {
      setEditSaving(false);
      hideLoading();
    }
  };

  // Remove treat
  const handleRemoveTreat = async () => {
    if (!editingTreat) return;

    setEditSaving(true);
    setEditError(null);
    showLoading("Removing treat...");

    try {
      await cancelSubscription(proxyBase, shop, editingTreat.id, "Customer removed treat", "", customerId);
      setEditSuccess(true);
      refresh();
      setTimeout(() => closeEditTreat(), 1200);
    } catch (err: any) {
      setEditError(err.message || "Failed to remove treat");
    } finally {
      setEditSaving(false);
      hideLoading();
    }
  };

  // Next delivery add-on edit handlers
  const openEditOnetime = (item: any) => {
    const productTitle = item.product_title || item.productTitle || "Add-on item";
    const info = variantIndex.get(String(item.shopify_variant_id || item.external_variant_id?.ecommerce || ""));
    setEditingOnetime({
      id: String(item.id),
      productTitle: info?.productTitle || productTitle,
      imageUrl: info?.imageUrl || null,
      price: Number(item.price || 0),
      quantity: Number(item.quantity || 1),
    });
    setOnetimeQuantity(Number(item.quantity || 1));
    setOnetimeSuccess(false);
    setOnetimeError(null);
  };

  const closeEditOnetime = () => {
    setEditingOnetime(null);
    setOnetimeQuantity(1);
    setOnetimeSuccess(false);
    setOnetimeError(null);
  };

  const handleSaveOnetimeQuantity = async () => {
    if (!editingOnetime || onetimeQuantity === editingOnetime.quantity) return;

    setOnetimeSaving(true);
    setOnetimeError(null);
    showLoading("Updating quantity...");

    try {
      const result = await updateOnetimeQuantity(proxyBase, shop, editingOnetime.id, onetimeQuantity, customerId);
      if (!result.ok) throw new Error(result.error || "Failed to update quantity");
      setOnetimeSuccess(true);
      // Refresh pending onetimes locally
      if (primaryAddressId) {
        const refreshed = await getOnetimesForAddress(proxyBase, shop, primaryAddressId, customerId);
        if (refreshed.ok) setPendingOnetimes(refreshed.onetimes || []);
      }
      // Notify parent to refresh its state
      onOnetimesChanged?.();
      setTimeout(() => closeEditOnetime(), 1200);
    } catch (err: any) {
      setOnetimeError(err.message || "Failed to update quantity");
    } finally {
      setOnetimeSaving(false);
      hideLoading();
    }
  };

  const handleRemoveOnetime = async () => {
    if (!editingOnetime) return;

    setOnetimeSaving(true);
    setOnetimeError(null);
    showLoading("Removing item...");

    try {
      const result = await deleteOnetime(proxyBase, shop, editingOnetime.id, customerId);
      if (!result.ok) throw new Error(result.error || "Failed to remove item");
      setOnetimeSuccess(true);
      setPendingOnetimes((prev) => prev.filter((o) => String(o.id) !== editingOnetime.id));
      // Notify parent to refresh its state
      onOnetimesChanged?.();
      setTimeout(() => closeEditOnetime(), 1200);
    } catch (err: any) {
      setOnetimeError(err.message || "Failed to remove item");
    } finally {
      setOnetimeSaving(false);
      hideLoading();
    }
  };

  // Show loading screen while fetching initial data
  if (loadingOnetimes) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
        }}
      >
        <PawsLoader action="loading" fullScreen={false} size="small" />
      </Box>
    );
  }

  return (
    <Stack spacing={{ xs: 2, md: 3 }}>
      {/* Header Card */}
      <QuizStyledCard>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <PageTitle
            title="Treats & Extras"
            subtitle="Add delicious treats to your deliveries"
          />

          {hasSubscriptions && (
            <QuizButton
              variant="primary"
              onClick={() => setAddWizardOpen(true)}
              startIcon={<AddIcon />}
              sx={{ flexShrink: 0 }}
            >
              Add Treats
            </QuizButton>
          )}
        </Box>
      </QuizStyledCard>

      {/* No Subscriptions State */}
      {!hasSubscriptions ? (
        <QuizStyledCard variant="white">
          <Box sx={{ textAlign: "center", py: { xs: 4, md: 6 } }}>
            <Box
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                borderRadius: "50%",
                backgroundColor: QUIZ.color.cream2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <CardGiftcardIcon
                sx={{ fontSize: { xs: 40, md: 50 }, color: QUIZ.color.brown }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 22 },
                fontWeight: 800,
                color: QUIZ.color.brown,
                mb: 1,
              }}
            >
              No active subscription
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                color: QUIZ.color.brown,
                opacity: 0.75,
                mb: 3,
              }}
            >
              Start a meal plan to add treats to your deliveries
            </Typography>
            <QuizButton variant="primary" href="/pages/quiz-1">
              Start the Quiz
            </QuizButton>
          </Box>
        </QuizStyledCard>
      ) : (
        <>
          {/* Next Delivery Add-ons */}
          {pendingOnetimes.length > 0 && (
            <QuizStyledCard variant="white">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 16, md: 18 },
                      fontWeight: 800,
                      color: QUIZ.color.brown,
                    }}
                  >
                    Next Delivery Add-ons
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 12, md: 13 },
                      color: QUIZ.color.brown,
                      opacity: 0.7,
                    }}
                  >
                    These items will be added to your next delivery only
                  </Typography>
                </Box>
                <Chip
                  icon={<ShoppingBagOutlinedIcon sx={{ fontSize: 16 }} />}
                  label="Next delivery only"
                  size="small"
                  sx={{
                    background: "#E6F0FF",
                    color: "#1A57D3",
                    fontWeight: 600,
                    "& .MuiChip-icon": { color: "#1A57D3" },
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                  },
                  gap: { xs: 1.5, md: 2 },
                }}
              >
                {pendingOnetimes.map((item) => {
                  const variantId = String(item.shopify_variant_id || item.external_variant_id?.ecommerce || "");
                  const info = variantIndex.get(variantId);
                  const productTitle = info?.productTitle || item.product_title || "Add-on item";
                  const imageUrl = info?.imageUrl || null;
                  const price = Number(item.price || 0);
                  const quantity = Number(item.quantity || 1);

                  return (
                    <Box
                      key={item.id}
                      sx={{
                        borderRadius: "14px",
                        border: "2px solid #1A57D3",
                        overflow: "hidden",
                        background: QUIZ.color.warmWhite,
                        boxShadow: "0px 2px 8px rgba(26, 87, 211, 0.1)",
                      }}
                    >
                      {/* Image */}
                      {imageUrl ? (
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={productTitle}
                          sx={{
                            width: "100%",
                            aspectRatio: "1",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            aspectRatio: "1",
                            background: "#E6F0FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ShoppingBagOutlinedIcon
                            sx={{ fontSize: 40, color: "#1A57D3", opacity: 0.3 }}
                          />
                        </Box>
                      )}

                      {/* Details */}
                      <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: 12, md: 13 },
                            color: QUIZ.color.brown,
                            lineHeight: 1.3,
                            mb: 0.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            minHeight: { xs: 32, md: 34 },
                          }}
                        >
                          {productTitle}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: 14, md: 15 },
                              fontWeight: 800,
                              color: "#1A57D3",
                            }}
                          >
                            £{(price * quantity).toFixed(2)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: { xs: 11, md: 12 },
                              color: QUIZ.color.brown,
                              opacity: 0.6,
                            }}
                          >
                            Qty: {quantity}
                          </Typography>
                        </Box>

                        {/* Edit button */}
                        <QuizButton
                          variant="secondary"
                          fullWidth
                          onClick={() => openEditOnetime(item)}
                          startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            py: 0.75,
                            fontSize: 12,
                          }}
                        >
                          Edit
                        </QuizButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* Total */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontWeight: 600, color: QUIZ.color.brown }}
                >
                  Add-ons Total
                </Typography>
                <Typography
                  sx={{ fontSize: 18, fontWeight: 800, color: "#1A57D3" }}
                >
                  £{pendingOnetimes.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 1)), 0).toFixed(2)}
                </Typography>
              </Box>
            </QuizStyledCard>
          )}

          {/* Your Treats - Read Only */}
          <QuizStyledCard variant="white">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 16, md: 18 },
                  fontWeight: 800,
                  color: QUIZ.color.brown,
                }}
              >
                Your Treats
              </Typography>
              {nextDeliveryDate && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "8px",
                    background: "#E6F0FF",
                  }}
                >
                  <LocalShippingIcon sx={{ fontSize: 14, color: "#1A57D3" }} />
                  <Typography
                    sx={{ fontSize: 12, color: "#1A57D3", fontWeight: 600 }}
                  >
                    {formatDate(nextDeliveryDate)}
                  </Typography>
                </Box>
              )}
            </Box>

            {currentTreatSubs.length === 0 ? (
              <Box sx={{ textAlign: "center", py: { xs: 3, md: 4 } }}>
                <CardGiftcardIcon
                  sx={{ fontSize: 40, color: QUIZ.color.brown, opacity: 0.3, mb: 1.5 }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: 14, md: 15 },
                    color: QUIZ.color.brown,
                    opacity: 0.6,
                    mb: 2,
                  }}
                >
                  No treats added yet
                </Typography>
                <QuizButton
                  variant="secondary"
                  onClick={() => setAddWizardOpen(true)}
                  startIcon={<AddIcon />}
                >
                  Add Your First Treat
                </QuizButton>
              </Box>
            ) : (
              <>
                {/* Treats as cards grid */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(3, 1fr)",
                    },
                    gap: { xs: 1.5, md: 2 },
                  }}
                >
                  {currentTreatSubs.map((treat) => (
                    <Box
                      key={treat.id}
                      sx={{
                        borderRadius: "14px",
                        border: "1px solid rgba(0,0,0,0.08)",
                        overflow: "hidden",
                        background: QUIZ.color.warmWhite,
                        boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
                      }}
                    >
                      {/* Image */}
                      {treat.imageUrl ? (
                        <Box
                          component="img"
                          src={treat.imageUrl}
                          alt={treat.productTitle}
                          sx={{
                            width: "100%",
                            aspectRatio: "1",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            aspectRatio: "1",
                            background: QUIZ.color.cream,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CardGiftcardIcon
                            sx={{ fontSize: 40, color: QUIZ.color.brown, opacity: 0.3 }}
                          />
                        </Box>
                      )}

                      {/* Details */}
                      <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: 12, md: 13 },
                            color: QUIZ.color.brown,
                            lineHeight: 1.3,
                            mb: 0.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            minHeight: { xs: 32, md: 34 },
                          }}
                        >
                          {treat.productTitle}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: 14, md: 15 },
                              fontWeight: 800,
                              color: QUIZ.color.brown,
                            }}
                          >
                            £{(treat.price * treat.quantity).toFixed(2)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: { xs: 11, md: 12 },
                              color: QUIZ.color.brown,
                              opacity: 0.6,
                            }}
                          >
                            Qty: {treat.quantity}
                          </Typography>
                        </Box>

                        {/* Edit button */}
                        <QuizButton
                          variant="secondary"
                          fullWidth
                          onClick={() => openEditTreat(treat)}
                          startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            py: 0.75,
                            fontSize: 12,
                          }}
                        >
                          Edit
                        </QuizButton>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Total */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                    pt: 2,
                    borderTop: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 14, fontWeight: 600, color: QUIZ.color.brown }}
                  >
                    Treats Total
                  </Typography>
                  <Typography
                    sx={{ fontSize: 18, fontWeight: 800, color: QUIZ.color.brown }}
                  >
                    £{treatsTotal.toFixed(2)}
                  </Typography>
                </Box>
              </>
            )}
          </QuizStyledCard>
        </>
      )}

      {/* Individual Treat Edit Dialog */}
      <Dialog
        open={!!editingTreat}
        onClose={editSaving ? undefined : closeEditTreat}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        {editingTreat && (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                borderBottom: "1px solid rgba(0,0,0,.08)",
                background: QUIZ.color.cream,
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: QUIZ.color.brown,
                }}
              >
                Edit Treat
              </Typography>
              <IconButton
                onClick={closeEditTreat}
                disabled={editSaving}
                size="small"
                sx={{ color: QUIZ.color.brown }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              {editSaving ? (
                <PawsLoader
                  action="saving"
                  fullScreen={false}
                  size="small"
                />
              ) : editSuccess ? (
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon />}
                  sx={{ borderRadius: "12px" }}
                >
                  Updated successfully!
                </Alert>
              ) : (
                <>
                  {editError && (
                    <Alert
                      severity="error"
                      sx={{ borderRadius: "12px", mb: 2 }}
                      onClose={() => setEditError(null)}
                    >
                      {editError}
                    </Alert>
                  )}

                  {/* Treat Info */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    {editingTreat.imageUrl ? (
                      <Box
                        component="img"
                        src={editingTreat.imageUrl}
                        alt={editingTreat.productTitle}
                        sx={{
                          width: 64,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: "10px",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "10px",
                          background: QUIZ.color.cream,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CardGiftcardIcon
                          sx={{ fontSize: 28, color: QUIZ.color.brown, opacity: 0.4 }}
                        />
                      </Box>
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 16,
                          color: QUIZ.color.brown,
                          mb: 0.5,
                        }}
                      >
                        {editingTreat.productTitle}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: QUIZ.color.brown,
                          fontWeight: 600,
                        }}
                      >
                        £{editingTreat.price.toFixed(2)} each
                      </Typography>
                    </Box>
                  </Box>

                  {/* Quantity Controls */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 2,
                      borderRadius: "12px",
                      background: QUIZ.color.cream,
                      border: "1px solid rgba(0,0,0,0.08)",
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 600, color: QUIZ.color.brown }}
                    >
                      Quantity
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        background: QUIZ.color.warmWhite,
                        borderRadius: "999px",
                        padding: "4px 12px",
                        border: "1px solid #E2E2E2",
                      }}
                    >
                      <IconButton
                        onClick={() => setEditQuantity(Math.max(1, editQuantity - 1))}
                        disabled={editQuantity <= 1 || editSaving}
                        size="small"
                        sx={{
                          width: 32,
                          height: 32,
                          color: QUIZ.color.brown,
                          "&.Mui-disabled": { opacity: 0.3 },
                        }}
                      >
                        <RemoveRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          minWidth: 28,
                          textAlign: "center",
                          fontSize: 16,
                          color: editQuantity !== editingTreat.quantity ? QUIZ.color.brown : QUIZ.color.brown,
                        }}
                      >
                        {editQuantity}
                      </Typography>
                      <IconButton
                        onClick={() => setEditQuantity(editQuantity + 1)}
                        disabled={editSaving}
                        size="small"
                        sx={{ width: 32, height: 32, color: QUIZ.color.brown }}
                      >
                        <AddRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* New Total */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                      Subtotal
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 800,
                        color: QUIZ.color.brown,
                      }}
                    >
                      £{(editingTreat.price * editQuantity).toFixed(2)}
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Stack spacing={1.5}>
                    <QuizButton
                      variant="primary"
                      fullWidth
                      onClick={handleSaveQuantity}
                      disabled={editQuantity === editingTreat.quantity}
                    >
                      Save Changes
                    </QuizButton>

                    <QuizButton
                      variant="secondary"
                      fullWidth
                      onClick={handleRemoveTreat}
                      startIcon={<DeleteOutlineIcon />}
                      sx={{
                        color: "#D32F2F",
                        borderColor: "#FFCDD2",
                        "&:hover": {
                          background: "#FFEBEE",
                          borderColor: "#EF9A9A",
                        },
                      }}
                    >
                      Remove Treat
                    </QuizButton>
                  </Stack>
                </>
              )}
            </Box>
          </>
        )}
      </Dialog>

      {/* Next Delivery Add-on Edit Dialog */}
      <Dialog
        open={!!editingOnetime}
        onClose={onetimeSaving ? undefined : closeEditOnetime}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        {editingOnetime && (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                borderBottom: "1px solid rgba(0,0,0,.08)",
                background: "#E6F0FF",
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#1A57D3",
                }}
              >
                Edit Add-on
              </Typography>
              <IconButton
                onClick={closeEditOnetime}
                disabled={onetimeSaving}
                size="small"
                sx={{ color: "#1A57D3" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              {onetimeSaving ? (
                <PawsLoader
                  action="saving"
                  fullScreen={false}
                  size="small"
                />
              ) : onetimeSuccess ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CheckCircleIcon
                    sx={{ fontSize: 48, color: "#1A57D3", mb: 2 }}
                  />
                  <Typography
                    sx={{ fontSize: 18, fontWeight: 700, color: "#1A57D3" }}
                  >
                    Updated!
                  </Typography>
                </Box>
              ) : (
                <>
                  {onetimeError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {onetimeError}
                    </Alert>
                  )}

                  {/* Product Info */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "12px",
                        overflow: "hidden",
                        flexShrink: 0,
                        background: "#E6F0FF",
                        border: "2px solid #1A57D3",
                      }}
                    >
                      {editingOnetime.imageUrl ? (
                        <Box
                          component="img"
                          src={editingOnetime.imageUrl}
                          alt={editingOnetime.productTitle}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ShoppingBagOutlinedIcon
                            sx={{ fontSize: 24, color: "#1A57D3", opacity: 0.5 }}
                          />
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 15,
                          color: QUIZ.color.brown,
                          mb: 0.5,
                        }}
                      >
                        {editingOnetime.productTitle}
                      </Typography>
                      <Chip
                        label="Next delivery only"
                        size="small"
                        sx={{
                          height: 22,
                          background: "#E6F0FF",
                          color: "#1A57D3",
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Quantity Controls */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 2,
                      borderRadius: "12px",
                      background: "#F5F9FF",
                      border: "1px solid rgba(26, 87, 211, 0.2)",
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 600, color: QUIZ.color.brown }}
                    >
                      Quantity
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        background: QUIZ.color.warmWhite,
                        borderRadius: "999px",
                        padding: "4px 12px",
                        border: "1px solid #1A57D3",
                      }}
                    >
                      <IconButton
                        onClick={() => setOnetimeQuantity(Math.max(1, onetimeQuantity - 1))}
                        disabled={onetimeQuantity <= 1 || onetimeSaving}
                        size="small"
                        sx={{
                          width: 32,
                          height: 32,
                          color: "#1A57D3",
                          "&.Mui-disabled": { opacity: 0.3 },
                        }}
                      >
                        <RemoveRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          minWidth: 28,
                          textAlign: "center",
                          fontSize: 16,
                          color: onetimeQuantity !== editingOnetime.quantity ? "#1A57D3" : QUIZ.color.brown,
                        }}
                      >
                        {onetimeQuantity}
                      </Typography>
                      <IconButton
                        onClick={() => setOnetimeQuantity(onetimeQuantity + 1)}
                        disabled={onetimeSaving}
                        size="small"
                        sx={{ width: 32, height: 32, color: "#1A57D3" }}
                      >
                        <AddRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* New Total */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                      Subtotal
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 800,
                        color: "#1A57D3",
                      }}
                    >
                      £{(editingOnetime.price * onetimeQuantity).toFixed(2)}
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Stack spacing={1.5}>
                    <QuizButton
                      variant="primary"
                      fullWidth
                      onClick={handleSaveOnetimeQuantity}
                      disabled={onetimeQuantity === editingOnetime.quantity}
                      sx={{
                        background: "#1A57D3",
                        "&:hover": { background: "#1548B3" },
                      }}
                    >
                      Save Changes
                    </QuizButton>

                    <QuizButton
                      variant="secondary"
                      fullWidth
                      onClick={handleRemoveOnetime}
                      startIcon={<DeleteOutlineIcon />}
                      sx={{
                        color: "#D32F2F",
                        borderColor: "#FFCDD2",
                        "&:hover": {
                          background: "#FFEBEE",
                          borderColor: "#EF9A9A",
                        },
                      }}
                    >
                      Remove Item
                    </QuizButton>
                  </Stack>
                </>
              )}
            </Box>
          </>
        )}
      </Dialog>

      {/* Add Treats Wizard */}
      <AddTreatsWizard
        open={addWizardOpen}
        onClose={() => setAddWizardOpen(false)}
        availableTreats={availableTreats}
        proxyBase={proxyBase}
        shop={shop}
        customerId={customerId}
        onSuccess={handleAddSuccess}
      />
    </Stack>
  );
}
